import { lazy } from "react";
import { createBrowserRouter, Outlet } from "react-router-dom";

import AppLayout from "./layout/AppLayout";
import AppHomePageLayout from "./layout/AppHomePageLayout";
import AppLoginSignupLayout from "./layout/AppLoginSignupLayout";
import App404Page from "./pages/app-404-page";
import { ErrorCatcher } from "./components/ErrorCatcher";
import { PrivateRoute } from "./components/PrivateRoute";
import { PageMetadata } from "./components/PageMetadata";

const RootRoute = () => (
  <>
    <PageMetadata />
    <Outlet />
  </>
);

// app
const AppSettingPage = lazy(() => import("src/pages/app-setting-page"));
const AppLoginWithWeb3Page = lazy(() => import("src/pages/app-login-with-web3-page"));
const AppLoginWithEmailPage = lazy(() => import("src/pages/app-login-with-email-page"));
const AppSignupWithWeb3Page = lazy(() => import("src/pages/app-signup-with-web3-page"));
const AppSignupWithEmailPage = lazy(() => import("src/pages/app-signup-with-email-page"));
const AppNeedVerifyEmailPage = lazy(() => import("src/pages/app-need-verify-email-page"));
const AppHomePage = lazy(() => import("src/pages/app-home-page"));
const AppPricingPage = lazy(() => import("src/pages/app-pricing-page"));
const AccountPreferencePage = lazy(() => import("src/pages/account-preference-page"));
const AccountProfilePage = lazy(() => import("src/pages/account-profile-page"));
const AppOverviewPage = lazy(() => import("src/pages/app-overview-page"));

// watchlist
const AccountWatchlistPage = lazy(() => import("src/pages/account-watchlist-page"));

// alert
const AlertPage = lazy(() => import("src/pages/alert-page"));

// starred transfers
const StarredTransfersPage = lazy(() => import("src/pages/starred-transfers-page"));

// api service
const ApiServicePage = lazy(() => import("src/pages/api-service-page"));
const ApiServiceBillingHistoryPage = lazy(() => import("src/pages/api-service-billing-history-page"));
const ApiServiceKeyStatusPage = lazy(() => import("src/pages/api-service-key-status-page"));
const ApiServicePlansPage = lazy(() => import("src/pages/api-service-plans-page"));
const ApiServicePaymentPage = lazy(() => import("src/pages/api-service-payment-page"));
const ApiServiceCryptoPaymentPage = lazy(() => import("src/pages/api-service-crypto-payment-page"));

// mcp
const McpPage = lazy(() => import("src/pages/mcp-page"));

// widgets
const WidgetsPage = lazy(() => import("src/pages/widgets-page"));
const WidgetShare = lazy(() => import("src/pages/widget-share"));
const WidgetsPublishHistoryPage = lazy(() => import("src/pages/widgets-publish-history-page"));
const WidgetsChangesUpdatePage = lazy(() => import("src/pages/widgets-changes-update-page"));
const WidgetsPublishPage = lazy(() => import("src/pages/widgets-publish-page"));
const WidgetsCustomizedNewPagePage = lazy(() => import("src/pages/widgets-customized-new-page-page"));
const WidgetsPublishPageEditPage = lazy(() => import("src/pages/widgets-publish-edit-page"));

// from email
const AppVerifyEmailPage = lazy(() => import("src/pages/from-email/app-verify-email-page"));
const AppInvalidTokenPage = lazy(() => import("src/pages/from-email/app-invalid-token-page"));
const AppForgotPasswordPage = lazy(() => import("src/pages/from-email/app-forgot-password-page"));
const AppDeleteAccountPage = lazy(() => import("src/pages/from-email/app-delete-account-page"));
const AppLinkEmailPasswordPage = lazy(() => import("src/pages/from-email/app-link-email-password-page"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRoute />,
    errorElement: <ErrorCatcher />,
    children: [
      // home page
      {
        index: true,
        handle: { title: "Subscan Open Platform", disableSuffix: true },
        element: (
          <AppHomePageLayout>
            <AppHomePage />
          </AppHomePageLayout>
        ),
      },
      {
        path: "pricing",
        handle: { title: "Pricing" },
        element: (
          <AppHomePageLayout>
            <AppPricingPage />
          </AppHomePageLayout>
        ),
      },
      {
        path: "overview",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Overview" },
            element: (
              <PrivateRoute>
                <AppOverviewPage />
              </PrivateRoute>
            ),
          },
        ],
      },
      // watchlist

      {
        path: "account_watchlist",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Watchlist" },
            element: (
              <PrivateRoute>
                <AccountWatchlistPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // alert
      {
        path: "alert",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Alert" },
            element: (
              <PrivateRoute>
                <AlertPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // starred transfers
      {
        path: "starred_transfer",
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <PrivateRoute>
                <StarredTransfersPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // api service
      {
        path: "api_service",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "API Service" },
            element: (
              <PrivateRoute needVerifyEmail>
                <ApiServicePage />
              </PrivateRoute>
            ),
          },
          {
            path: "billing_history",
            handle: { title: "Billing History" },
            element: (
              <PrivateRoute needVerifyEmail>
                <ApiServiceBillingHistoryPage />
              </PrivateRoute>
            ),
          },
          {
            path: "subscan_status",
            handle: { title: "API Key Status" },
            element: (
              <PrivateRoute needVerifyEmail>
                <ApiServiceKeyStatusPage />
              </PrivateRoute>
            ),
          },
          {
            path: "plans",
            handle: { title: "API Plans" },
            element: (
              <PrivateRoute needVerifyEmail>
                <ApiServicePlansPage />
              </PrivateRoute>
            ),
          },
          {
            path: "payment",
            element: (
              <PrivateRoute needVerifyEmail>
                <Outlet />
              </PrivateRoute>
            ),
            children: [
              {
                index: true,
                handle: { title: "Payment" },
                element: <ApiServicePaymentPage />,
              },
              {
                path: "crypto_payment",
                handle: { title: "Crypto Payment" },
                element: <ApiServiceCryptoPaymentPage />,
              },
            ],
          },
        ],
      },

      // mcp chat
      {
        path: "mcp",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "MCP Assistant" },
            element: <McpPage />,
          },
        ],
      },

      // widget
      {
        path: "widget/:id",
        handle: { title: "Widget Share" },
        element: <WidgetShare />,
      },

      // widgets
      {
        path: "widgets",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Widgets" },
            element: (
              <PrivateRoute needVerifyEmail>
                <WidgetsPage />
              </PrivateRoute>
            ),
          },
          {
            path: "history",
            handle: { title: "Publish History" },
            element: (
              <PrivateRoute needVerifyEmail>
                <WidgetsPublishHistoryPage />
              </PrivateRoute>
            ),
          },
          {
            path: "update_widget_changes",
            handle: { title: "Widget Change Log" },
            element: (
              <PrivateRoute needAdminRole>
                <WidgetsChangesUpdatePage />
              </PrivateRoute>
            ),
          },
          {
            path: "publish",
            element: (
              <PrivateRoute needAdminRole>
                <Outlet />
              </PrivateRoute>
            ),
            children: [
              {
                index: true,
                handle: { title: "Publish Widget" },
                element: <WidgetsPublishPage />,
              },
              {
                path: "customized_new_page",
                handle: { title: "Customized Widget" },
                element: <WidgetsCustomizedNewPagePage />,
              },
              {
                path: "edit",
                handle: { title: "Edit Widget" },
                element: <WidgetsPublishPageEditPage />,
              },
            ],
          },
        ],
      },

      {
        path: "setting",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Settings" },
            element: (
              <PrivateRoute>
                <AppSettingPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      {
        path: "preference",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Preferences" },
            element: (
              <PrivateRoute>
                <AccountPreferencePage />
              </PrivateRoute>
            ),
          },
        ],
      },

      {
        path: "profile",
        element: <AppLayout />,
        children: [
          {
            index: true,
            handle: { title: "Profile" },
            element: (
              <PrivateRoute>
                <AccountProfilePage />
              </PrivateRoute>
            ),
          },
        ],
      },

      {
        path: "login",
        element: <AppLoginSignupLayout type="login" />,
        children: [
          {
            index: true,
            handle: { title: "Login" },
            element: <AppLoginWithEmailPage />,
          },
          {
            path: "email",
            handle: { title: "Login with Email" },
            element: <AppLoginWithEmailPage />,
          },
          {
            path: "web3",
            handle: { title: "Login with Web3" },
            element: <AppLoginWithWeb3Page />,
          },
        ],
      },
      {
        path: "signup",
        element: <AppLoginSignupLayout type="signup" />,
        children: [
          {
            index: true,
            handle: { title: "Sign Up" },
            element: <AppSignupWithEmailPage />,
          },
          {
            path: "email",
            handle: { title: "Sign Up with Email" },
            element: <AppSignupWithEmailPage />,
          },
          {
            path: "web3",
            handle: { title: "Sign Up with Web3" },
            element: <AppSignupWithWeb3Page />,
          },
        ],
      },
      {
        path: "need_verify_email",
        handle: { title: "Email Verification Required" },
        element: (
          <PrivateRoute>
            <AppNeedVerifyEmailPage />
          </PrivateRoute>
        ),
      },
      {
        path: "verify_email/:token",
        handle: { title: "Verify Email" },
        element: (
          <PrivateRoute>
            <AppVerifyEmailPage />
          </PrivateRoute>
        ),
      },
      {
        path: "forgot_password/:token",
        handle: { title: "Reset Password" },
        element: <AppForgotPasswordPage />,
      },
      {
        path: "delete_account/:token",
        handle: { title: "Delete Account" },
        element: <AppDeleteAccountPage />,
      },
      {
        path: "link_email_password/:token",
        handle: { title: "Link Email Password" },
        element: <AppLinkEmailPasswordPage />,
      },
      {
        path: "invalid_token",
        handle: { title: "Invalid Token" },
        element: <AppInvalidTokenPage />,
      },
    ],
  },
  {
    path: "*",
    handle: { title: "Page Not Found" },
    element: <App404Page />,
  },
]);
